import { useEffect } from "react";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { SimplifyProvider } from "@/components/od/simplify-context";
import Header from "@/components/od/Header";
import Footer from "@/components/od/Footer";
import SimplifyModal from "@/components/od/SimplifyModal";
import HomePage from "@/pages/HomePage";
import SolutionsPage from "@/pages/SolutionsPage";
import JournalHubPage from "@/pages/JournalHubPage";
import ArticleView from "@/pages/ArticleView";
import DemosHubPage from "@/pages/DemosHubPage";
import RevenueDemoPage from "@/pages/RevenueDemoPage";
import BooksDemoPage from "@/pages/BooksDemoPage";
import PricingPage from "@/pages/PricingPage";
import TrialPage from "@/pages/TrialPage";
import AboutPage from "@/pages/AboutPage";
import TrustPage from "@/pages/TrustPage";
import IndustryPage from "@/pages/IndustryPage";
import GetStartedPage from "@/pages/GetStartedPage";
import ArticleViewFr from "@/pages/ArticleViewFr";
import GardezVosOutilsPage from "@/pages/GardezVosOutilsPage";
import Loi25Page from "@/pages/Loi25Page";
import RfiPage from "@/pages/RfiPage";
import FrHomePage from "@/pages/FrHomePage";
import AuditGratuitPage from "@/pages/AuditGratuitPage";
import EsHomePage from "@/pages/EsHomePage";
import EsHerramientasPage from "@/pages/EsHerramientasPage";
import EsRfiPage from "@/pages/EsRfiPage";
import EsRevisionPage from "@/pages/EsRevisionPage";
import ArticleViewEs from "@/pages/ArticleViewEs";
import ZohoIntegrationsPage from "@/pages/ZohoIntegrationsPage";
import MiamiPage from "@/pages/MiamiPage";
import EsMiamiPage from "@/pages/EsMiamiPage";
import FrMiamiPage from "@/pages/FrMiamiPage";
import AdminPage from "@/pages/AdminPage";
import LocalCityPage from "@/pages/LocalCityPage";
import ClusterPage from "@/pages/ClusterPage";
import { ROUTE_DESCRIPTIONS, ROUTE_OG_TITLES, DEFAULT_OG_TITLE } from "@/data/seoMeta";

// Per-route SEO for crawlers: the static head canonical/description/OG point at "/";
// this updates them to the current route so each page declares its own URL + share card.
function CanonicalManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    const url = `https://oceandata.ca${pathname}`;
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", url);
    const desc = ROUTE_DESCRIPTIONS[pathname] || ROUTE_DESCRIPTIONS["/"];
    const title = ROUTE_OG_TITLES[pathname] || DEFAULT_OG_TITLE;
    const setMeta = (sel, attr, val) => {
      const el = document.querySelector(sel);
      if (el) el.setAttribute(attr, val);
    };
    setMeta('meta[name="description"]', "content", desc);
    setMeta('meta[property="og:url"]', "content", url);
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", desc);
    setMeta('meta[name="twitter:title"]', "content", title);
    setMeta('meta[name="twitter:description"]', "content", desc);
  }, [pathname]);
  return null;
}

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => {
        document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
      }, 400);
      return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <SimplifyProvider>
      <BrowserRouter>
        <ScrollManager />
        <CanonicalManager />
        <div className="bg-[#F8FAFC] text-slate-800 font-sans antialiased">
          <Header />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/solutions" element={<SolutionsPage />} />
            <Route path="/journal" element={<JournalHubPage />} />
            <Route path="/journal/:slug" element={<ArticleView />} />
            <Route path="/demos" element={<DemosHubPage />} />
            <Route path="/demos/revenue" element={<RevenueDemoPage />} />
            <Route path="/demos/books" element={<BooksDemoPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/how-it-works" element={<TrialPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/trust" element={<TrustPage />} />
            <Route path="/industries/:slug" element={<IndustryPage />} />
            <Route path="/get-started" element={<GetStartedPage />} />
            <Route path="/fr/journal/:slug" element={<ArticleViewFr />} />
            <Route path="/fr/gardez-vos-outils" element={<GardezVosOutilsPage />} />
            <Route path="/fr/loi-25-donnees-clients" element={<Loi25Page />} />
            <Route path="/fr/reponse-rfi-quelques-heures" element={<RfiPage />} />
            <Route path="/fr" element={<FrHomePage />} />
            <Route path="/fr/audit-gratuit-15-minutes" element={<AuditGratuitPage />} />
            <Route path="/es" element={<EsHomePage />} />
            <Route path="/es/mantenga-sus-herramientas" element={<EsHerramientasPage />} />
            <Route path="/es/respuesta-rfi-en-horas" element={<EsRfiPage />} />
            <Route path="/es/revision-gratuita-15-minutos" element={<EsRevisionPage />} />
            <Route path="/es/journal/:slug" element={<ArticleViewEs />} />
            <Route path="/fr/tarifs" element={<PricingPage lang="fr" />} />
            <Route path="/es/precios" element={<PricingPage lang="es" />} />
            <Route path="/fr/comment-ca-marche" element={<TrialPage lang="fr" />} />
            <Route path="/es/como-funciona" element={<TrialPage lang="es" />} />
            <Route path="/zoho-integrations-montreal" element={<ZohoIntegrationsPage />} />
            <Route path="/fr/solutions" element={<SolutionsPage lang="fr" />} />
            <Route path="/es/soluciones" element={<SolutionsPage lang="es" />} />
            <Route path="/fr/a-propos" element={<AboutPage lang="fr" />} />
            <Route path="/es/nosotros" element={<AboutPage lang="es" />} />
            <Route path="/fr/demos" element={<DemosHubPage lang="fr" />} />
            <Route path="/es/demos" element={<DemosHubPage lang="es" />} />
            <Route path="/miami" element={<MiamiPage />} />
            <Route path="/es/miami" element={<EsMiamiPage />} />
            <Route path="/fr/miami" element={<FrMiamiPage />} />
            {/* Local landing pages (trilingual) */}
            <Route path="/phoenix" element={<LocalCityPage city="phoenix" lang="en" />} />
            <Route path="/fr/phoenix" element={<LocalCityPage city="phoenix" lang="fr" />} />
            <Route path="/es/phoenix" element={<LocalCityPage city="phoenix" lang="es" />} />
            <Route path="/scottsdale" element={<LocalCityPage city="scottsdale" lang="en" />} />
            <Route path="/fr/scottsdale" element={<LocalCityPage city="scottsdale" lang="fr" />} />
            <Route path="/es/scottsdale" element={<LocalCityPage city="scottsdale" lang="es" />} />
            <Route path="/fort-lauderdale" element={<LocalCityPage city="fort-lauderdale" lang="en" />} />
            <Route path="/fr/fort-lauderdale" element={<LocalCityPage city="fort-lauderdale" lang="fr" />} />
            <Route path="/es/fort-lauderdale" element={<LocalCityPage city="fort-lauderdale" lang="es" />} />
            <Route path="/montreal" element={<LocalCityPage city="montreal" lang="en" />} />
            <Route path="/fr/montreal" element={<LocalCityPage city="montreal" lang="fr" />} />
            <Route path="/es/montreal" element={<LocalCityPage city="montreal" lang="es" />} />
            {/* Software cluster pages (trilingual) */}
            <Route path="/zoho-fsm" element={<ClusterPage slug="zoho-fsm" lang="en" />} />
            <Route path="/fr/zoho-fsm" element={<ClusterPage slug="zoho-fsm" lang="fr" />} />
            <Route path="/es/zoho-fsm" element={<ClusterPage slug="zoho-fsm" lang="es" />} />
            <Route path="/zoho-books-vs-quickbooks" element={<ClusterPage slug="zoho-books-vs-quickbooks" lang="en" />} />
            <Route path="/fr/zoho-books-vs-quickbooks" element={<ClusterPage slug="zoho-books-vs-quickbooks" lang="fr" />} />
            <Route path="/es/zoho-books-vs-quickbooks" element={<ClusterPage slug="zoho-books-vs-quickbooks" lang="es" />} />
            <Route path="/zoho-iot-connected-buildings" element={<ClusterPage slug="zoho-iot-connected-buildings" lang="en" />} />
            <Route path="/fr/zoho-iot-connected-buildings" element={<ClusterPage slug="zoho-iot-connected-buildings" lang="fr" />} />
            <Route path="/es/zoho-iot-connected-buildings" element={<ClusterPage slug="zoho-iot-connected-buildings" lang="es" />} />
            <Route path="/zuper-field-service" element={<ClusterPage slug="zuper-field-service" lang="en" />} />
            <Route path="/fr/zuper-field-service" element={<ClusterPage slug="zuper-field-service" lang="fr" />} />
            <Route path="/es/zuper-field-service" element={<ClusterPage slug="zuper-field-service" lang="es" />} />
            <Route path="/zoho-fsm-vs-zuper" element={<ClusterPage slug="zoho-fsm-vs-zuper" lang="en" />} />
            <Route path="/fr/zoho-fsm-vs-zuper" element={<ClusterPage slug="zoho-fsm-vs-zuper" lang="fr" />} />
            <Route path="/es/zoho-fsm-vs-zuper" element={<ClusterPage slug="zoho-fsm-vs-zuper" lang="es" />} />
            <Route path="/zoho-projects-construction" element={<ClusterPage slug="zoho-projects-construction" lang="en" />} />
            <Route path="/fr/zoho-projects-construction" element={<ClusterPage slug="zoho-projects-construction" lang="fr" />} />
            <Route path="/es/zoho-projects-construction" element={<ClusterPage slug="zoho-projects-construction" lang="es" />} />
            <Route path="/zoho-fsm-accounting" element={<ClusterPage slug="zoho-fsm-accounting" lang="en" />} />
            <Route path="/fr/zoho-fsm-accounting" element={<ClusterPage slug="zoho-fsm-accounting" lang="fr" />} />
            <Route path="/es/zoho-fsm-accounting" element={<ClusterPage slug="zoho-fsm-accounting" lang="es" />} />
            <Route path="/admin" element={<AdminPage />} />
          </Routes>
          <Footer />
          <SimplifyModal />
          <Toaster position="bottom-center" />
        </div>
      </BrowserRouter>
    </SimplifyProvider>
  );
}

export default App;
